import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import { CommentsSection } from "./CommentsSection";

type Status = "requested" | "scheduled" | "completed" | "cancelled";
type FeedbackType = "reschedule_request" | "no_show";

export interface VisitDetail {
  _id: string;
  title: string;
  description: string | null;
  status: Status;
  scheduledFor: string | null;
  completedAt: number | null;
  recurringIntervalDays: number | null;
  contractor: { name: string; serviceType: string } | null;
  unit: { label: string } | null;
}

interface FeedbackItem {
  _id: string;
  type: FeedbackType;
  message: string | null;
  preferredDate: string | null;
  createdAt: number;
  from: {
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
}

// Read-only view of a calendar visit. Tenants can submit feedback
// (request a reschedule on future visits, report a no-show on past
// visits); landlords get Edit / Mark complete / Cancel actions in
// the footer. Both see the feedback log.
export function VisitDetailDialog({
  open,
  onClose,
  task,
  isLandlord,
  onEdit,
}: {
  open: boolean;
  onClose: () => void;
  task: VisitDetail | null;
  isLandlord: boolean;
  onEdit: () => void;
}) {
  const markCompleted = useMutation(anyApi.maintenance.markCompleted);
  const cancel = useMutation(anyApi.maintenance.cancel);
  const feedback = useQuery(
    anyApi.feedback.listForTask,
    open && task ? { taskId: task._id } : "skip",
  ) as FeedbackItem[] | undefined;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!task) return null;

  const handleComplete = async () => {
    setBusy(true);
    setError(null);
    try {
      await markCompleted({ taskId: task._id });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Cancel this visit? It'll stop showing on the calendar.")) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cancel({ taskId: task._id });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={task.title} width="lg">
      <div className="px-6 py-5 space-y-5">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={task.status} />
          {task.recurringIntervalDays && (
            <span className="text-xs text-slate-500">
              Repeats every {formatInterval(task.recurringIntervalDays)}
            </span>
          )}
        </div>

        {task.description && (
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {task.description}
          </p>
        )}

        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-slate-500">When</dt>
          <dd className="text-slate-900">
            {task.scheduledFor ? formatDateTime(task.scheduledFor) : "TBD"}
          </dd>
          {task.completedAt && (
            <>
              <dt className="text-slate-500">Completed</dt>
              <dd className="text-slate-900">
                {formatTimestamp(task.completedAt)}
              </dd>
            </>
          )}
          <dt className="text-slate-500">Scope</dt>
          <dd className="text-slate-900">
            {task.unit ? task.unit.label : "Property-wide"}
          </dd>
          {task.contractor && (
            <>
              <dt className="text-slate-500">Contractor</dt>
              <dd className="text-slate-900">
                {task.contractor.name} · {task.contractor.serviceType}
              </dd>
            </>
          )}
        </dl>

        <FeedbackSection
          task={task}
          isLandlord={isLandlord}
          feedback={feedback}
        />

        <CommentsSection taskId={task._id} />

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 -mx-6 px-6">
          {isLandlord &&
            task.status !== "cancelled" &&
            task.status !== "completed" && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={busy}
                className="mr-auto text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 transition-colors"
              >
                {task.status === "requested" ? "Cancel request" : "Cancel visit"}
              </button>
            )}
          {isLandlord && task.status === "scheduled" && (
            <button
              type="button"
              onClick={handleComplete}
              disabled={busy}
              className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Mark complete
            </button>
          )}
          {isLandlord && task.status !== "cancelled" && (
            <button
              type="button"
              onClick={onEdit}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

function FeedbackSection({
  task,
  isLandlord,
  feedback,
}: {
  task: VisitDetail;
  isLandlord: boolean;
  feedback: FeedbackItem[] | undefined;
}) {
  const [activeForm, setActiveForm] = useState<FeedbackType | null>(null);

  const isTenant = !isLandlord;
  const now = Date.now();
  const scheduledMs = task.scheduledFor
    ? new Date(task.scheduledFor).getTime()
    : null;
  const isFuture = scheduledMs !== null && scheduledMs > now;
  const isPast = scheduledMs !== null && scheduledMs <= now;
  // Tenant feedback only makes sense while the visit is still
  // "scheduled". Once it's completed or cancelled, the conversation
  // is over.
  const canSubmit = isTenant && task.status === "scheduled";

  const showAny = (feedback && feedback.length > 0) || canSubmit;
  if (!showAny) return null;

  return (
    <section className="space-y-3">
      <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
        Feedback
      </h3>

      {feedback && feedback.length > 0 && (
        <ul className="space-y-2">
          {feedback.map((item) => (
            <FeedbackRow key={item._id} item={item} />
          ))}
        </ul>
      )}

      {canSubmit && !activeForm && (
        <div className="flex flex-wrap gap-2 pt-1">
          {isFuture && (
            <button
              type="button"
              onClick={() => setActiveForm("reschedule_request")}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Request reschedule
            </button>
          )}
          {isPast && (
            <button
              type="button"
              onClick={() => setActiveForm("no_show")}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Report no-show
            </button>
          )}
        </div>
      )}

      {activeForm === "reschedule_request" && (
        <RescheduleForm
          taskId={task._id}
          onClose={() => setActiveForm(null)}
        />
      )}
      {activeForm === "no_show" && (
        <NoShowForm taskId={task._id} onClose={() => setActiveForm(null)} />
      )}
    </section>
  );
}

function FeedbackRow({ item }: { item: FeedbackItem }) {
  const author = item.from
    ? item.from.firstName ?? item.from.email
    : "Unknown";
  return (
    <li className="rounded-md border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <FeedbackBadge type={item.type} />
        <span className="text-xs text-slate-500">
          {author} · {timeAgo(item.createdAt)}
        </span>
      </div>
      {item.preferredDate && (
        <p className="mt-1 text-sm text-slate-700">
          Preferred new date:{" "}
          <span className="text-slate-900 font-medium">
            {new Date(`${item.preferredDate}T00:00:00`).toLocaleDateString(
              "en-US",
              { month: "short", day: "numeric", year: "numeric" },
            )}
          </span>
        </p>
      )}
      {item.message && (
        <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">
          {item.message}
        </p>
      )}
    </li>
  );
}

function RescheduleForm({
  taskId,
  onClose,
}: {
  taskId: string;
  onClose: () => void;
}) {
  const [preferredDate, setPreferredDate] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = useMutation(anyApi.feedback.requestReschedule);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await submit({
        taskId,
        preferredDate: preferredDate || undefined,
        message: message || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-slate-200 bg-white px-3 py-3 space-y-3"
    >
      <p className="text-xs font-medium text-slate-700">Request reschedule</p>
      <label className="block">
        <span className="block text-xs text-slate-500 mb-1">
          Preferred new date (optional)
        </span>
        <input
          type="date"
          value={preferredDate}
          onChange={(e) => setPreferredDate(e.target.value)}
          className={smallInputCls}
        />
      </label>
      <label className="block">
        <span className="block text-xs text-slate-500 mb-1">
          Message (optional)
        </span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          placeholder="Why? Anything that helps the landlord pick a new date."
          className={`${smallInputCls} resize-none`}
        />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {submitting ? "Sending…" : "Send request"}
        </button>
      </div>
    </form>
  );
}

function NoShowForm({
  taskId,
  onClose,
}: {
  taskId: string;
  onClose: () => void;
}) {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = useMutation(anyApi.feedback.reportNoShow);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await submit({ taskId, message: message || undefined });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-slate-200 bg-white px-3 py-3 space-y-3"
    >
      <p className="text-xs font-medium text-slate-700">Report no-show</p>
      <label className="block">
        <span className="block text-xs text-slate-500 mb-1">
          What happened? (optional)
        </span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          placeholder="Did anyone show up? Was there contact? Anything else useful."
          className={`${smallInputCls} resize-none`}
        />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {submitting ? "Sending…" : "Submit report"}
        </button>
      </div>
    </form>
  );
}

const smallInputCls =
  "w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

function FeedbackBadge({ type }: { type: FeedbackType }) {
  const map: Record<FeedbackType, { label: string; className: string }> = {
    reschedule_request: {
      label: "Reschedule requested",
      className: "bg-amber-50 text-amber-700 ring-amber-200",
    },
    no_show: {
      label: "No-show reported",
      className: "bg-rose-50 text-rose-700 ring-rose-200",
    },
  };
  const { label, className } = map[type];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${className}`}
    >
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const styles: Record<Status, string> = {
    scheduled: "bg-blue-50 text-blue-700 ring-blue-200",
    requested: "bg-amber-50 text-amber-700 ring-amber-200",
    completed: "bg-slate-100 text-slate-600 ring-slate-200",
    cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles[status]}`}
    >
      {status}
    </span>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatInterval(days: number): string {
  if (days % 365 === 0) {
    const y = days / 365;
    return y === 1 ? "year" : `${y} years`;
  }
  if (days % 30 === 0) {
    const m = days / 30;
    return m === 1 ? "month" : `${m} months`;
  }
  return `${days} days`;
}

function timeAgo(ts: number): string {
  const diffSec = Math.floor((Date.now() - ts) / 1000);
  if (diffSec < 60) return "just now";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
